import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Building2, Loader2, Save } from 'lucide-react'
import api from '../../core/services/api'

interface Azienda {
  id: string
  codice: string
  nome: string
  is_active: boolean
}

interface UtenteAziendaAssegnazione {
  id: string
  user_id: string
  azienda_id: string
  azienda_codice?: string
  azienda_nome?: string
  role: string
  is_active: boolean
}

interface Props {
  utente: { id: string; username: string; email: string }
  onClose: () => void
  onToast: (msg: string, type: 'success' | 'error') => void
}

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'Utente' },
  { value: 'readonly', label: 'Sola Lettura' },
]

export default function UtenteAziendeModal({ utente, onClose, onToast }: Props) {
  const [aziende, setAziende] = useState<Azienda[]>([])
  const [assegnazioni, setAssegnazioni] = useState<UtenteAziendaAssegnazione[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Per nuova assegnazione
  const [showAdd, setShowAdd] = useState(false)
  const [newAziendaId, setNewAziendaId] = useState('')
  const [newRole, setNewRole] = useState('user')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [azRes, assRes] = await Promise.all([
        api.get('/v1/anagrafiche/aziende'),
        api.get(`/v1/anagrafiche/utenti/${utente.id}/aziende`),
      ])
      setAziende(azRes.data)
      setAssegnazioni(assRes.data)
    } catch (err: any) {
      onToast('Errore caricamento dati', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Aziende non ancora assegnate
  const aziendeDisponibili = aziende.filter(
    az => az.is_active && !assegnazioni.find(a => a.azienda_id === az.id)
  )

  const handleAdd = async () => {
    if (!newAziendaId) return
    setSaving(true)
    try {
      await api.post(`/v1/anagrafiche/utenti/${utente.id}/aziende`, {
        azienda_id: newAziendaId,
        role: newRole,
      })
      onToast('Azienda assegnata', 'success')
      setShowAdd(false)
      setNewAziendaId('')
      setNewRole('user')
      await loadData()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore assegnazione', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateRole = async (ass: UtenteAziendaAssegnazione, newRoleValue: string) => {
    try {
      await api.put(`/v1/anagrafiche/utenti/${utente.id}/aziende/${ass.id}`, {
        azienda_id: ass.azienda_id,
        role: newRoleValue,
        is_active: ass.is_active,
      })
      onToast('Ruolo aggiornato', 'success')
      await loadData()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore aggiornamento', 'error')
    }
  }

  const handleToggleActive = async (ass: UtenteAziendaAssegnazione) => {
    try {
      await api.put(`/v1/anagrafiche/utenti/${utente.id}/aziende/${ass.id}`, {
        azienda_id: ass.azienda_id,
        role: ass.role,
        is_active: !ass.is_active,
      })
      onToast(ass.is_active ? 'Disattivato' : 'Attivato', 'success')
      await loadData()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    }
  }

  const handleRemove = async (ass: UtenteAziendaAssegnazione) => {
    if (!confirm(`Rimuovere ${utente.username} da ${ass.azienda_nome}?`)) return
    try {
      await api.delete(`/v1/anagrafiche/utenti/${utente.id}/aziende/${ass.id}`)
      onToast('Assegnazione rimossa', 'success')
      await loadData()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore rimozione', 'error')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-primary-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Aziende di {utente.username}</h2>
              <p className="text-sm text-gray-500">{utente.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {assegnazioni.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nessuna azienda assegnata
                </p>
              ) : (
                assegnazioni.map((ass) => (
                  <div
                    key={ass.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      ass.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">
                        {ass.azienda_codice?.substring(0, 2).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900">{ass.azienda_nome}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={ass.role}
                          onChange={(e) => handleUpdateRole(ass, e.target.value)}
                          className="text-xs border rounded px-2 py-0.5"
                        >
                          {ROLES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleToggleActive(ass)}
                          className={`text-xs px-2 py-0.5 rounded ${
                            ass.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {ass.is_active ? 'Attivo' : 'Disattivo'}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => handleRemove(ass)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Rimuovi"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}

              {/* Aggiungi nuova assegnazione */}
              {aziendeDisponibili.length > 0 && !showAdd && (
                <button
                  onClick={() => setShowAdd(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Aggiungi azienda</span>
                </button>
              )}

              {showAdd && (
                <div className="p-3 rounded-lg border-2 border-primary-300 bg-primary-50 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={newAziendaId}
                      onChange={(e) => setNewAziendaId(e.target.value)}
                      className="text-sm border rounded-lg px-3 py-2"
                    >
                      <option value="">Seleziona azienda...</option>
                      {aziendeDisponibili.map(az => (
                        <option key={az.id} value={az.id}>{az.nome}</option>
                      ))}
                    </select>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      className="text-sm border rounded-lg px-3 py-2"
                    >
                      {ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAdd}
                      disabled={!newAziendaId || saving}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Assegna
                    </button>
                    <button
                      onClick={() => { setShowAdd(false); setNewAziendaId(''); }}
                      className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-100"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
